import {
  ActionBar,
  ChakraHStack,
  FAIconName,
  FireflyButton,
  ModalV2,
  useToastMessage,
} from '@equip.health/ui';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import { AddProblemModalSelectProblemStep } from '~/components/maud/patients/profile/tabs/careplan/AddProblemModalSelectProblemStep';
import { useEffect, useState } from 'react';
import { RefCarePlanProblemResponse } from '~/lib/types/carePlan/RefCarePlanProblemResponse';
import { AddProblemModalManifestationsStep } from '~/components/maud/patients/profile/tabs/careplan/AddProblemModalManifestationsStep';
import Loading from '~/pages/loading';
import {
  useGetRefCarePlanFrequenciesEnumQuery,
  useGetRefCarePlanGoalsEnumQuery,
  useGetRefCarePlanInterventionsEnumQuery,
  useGetRefCarePlanManifestationsEnumQuery,
} from '~/store/services/refCarePlanEnums';
import {
  useCreateProblemMutation,
  useCreateCarePlanMutation,
} from '~/store/services/carePlan';
import {
  CreateProblemRequest,
  CarePlanResponse,
} from '~/lib/types/carePlan/carePlans';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import {
  useGetFormTreatmentAttributesQuery,
  useGetModalityTreatmentAttributesQuery,
} from '~/store/services/treatmentAttributes';
import { CarePlanInterventionStatus } from '~/lib/types/carePlan/CarePlanInterventionStatus';
import { usePhaseStageCodesForModality } from '~/lib/hooks/useGetPhaseStagesForModality';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { ManifestationCodeToEditableDataMap } from '~/lib/types/carePlan/ManifestationCodeToEditableDataMap';
import { RESOLVED_FREQUENCY_CODES } from '~/lib/constants/carePlan.constants';
import { skipToken } from '@reduxjs/toolkit/query';

const CREATE_PROBLEM_TOAST_ARGS = {
  id: 'save-problem-unexpected-error',
  icon: FAIconName.exclamationTriangleRegular,
  text: 'We encountered an unexpected error. Please try again.',
  variant: 'critical',
} as const;

const TOP_LEVEL_ID = 'add-problem-modal';

export type AddProblemModalProps = {
  closeModal: () => void;
  patientId: string;
  problemTypeCode: ProblemTypeCode;
  activeCarePlan?: CarePlanResponse;
  treatmentEpisodeId: string;
};

/**
 * @deprecated Superseded by the M4 add-problem modal redesign. New work should use the multi-problem
 * selection + `AddProblemDetailsModal` flow orchestrated by `CarePlanProblemDialogManager`. This
 * single-problem modal is retained only for the callers that have not yet migrated.
 */
export const AddProblemModal = ({
  closeModal,
  patientId,
  problemTypeCode,
  activeCarePlan,
  treatmentEpisodeId,
}: AddProblemModalProps) => {
  const { carePlanM4ModalityOptional: isModalityOptional } =
    useFlags<LDFlags>();
  const { trackEvent } = useAnalytics();

  const [selectedProblem, setSelectedProblem] =
    useState<RefCarePlanProblemResponse | null>(null);
  const [problemDescription, setProblemDescription] = useState('');
  const [problemFrequencyCode, setProblemFrequencyCode] = useState<
    string | undefined
  >();
  const [selectedManifestationsMap, setSelectedManifestationsMap] =
    useState<ManifestationCodeToEditableDataMap>({});
  const { createToast } = useToastMessage();

  const { data: modalities } = useGetModalityTreatmentAttributesQuery(
    treatmentEpisodeId,
    {
      skip: !!activeCarePlan,
    },
  );
  const { data: forms } =
    useGetFormTreatmentAttributesQuery(treatmentEpisodeId);

  const { data: goalsForProblem, isLoading: isGetGoalsLoading } =
    useGetRefCarePlanGoalsEnumQuery(
      selectedProblem ? { problemCodes: [selectedProblem.code] } : skipToken,
    );

  const {
    data: manifestationsForED,
    isError: isGetManifestationsError,
    isLoading: isGetManifestationsLoading,
  } = useGetRefCarePlanManifestationsEnumQuery(
    selectedProblem ? { problemCodes: [selectedProblem.code] } : skipToken,
  );

  const { isLoading: isFrequenciesLoading } =
    useGetRefCarePlanFrequenciesEnumQuery(undefined, {
      skip: !selectedProblem,
    });

  const activeModality = modalities?.find(
    (modality) => modality.endDate === null,
  );

  const activeForm = forms?.find((form) => form.endDate === null);

  const modalityCode =
    activeCarePlan?.treatmentEpisodeModality?.modality.code ||
    activeModality?.modality.code;

  const {
    phaseStages: phaseStagesForModality,
    isLoading: isGetPhaseStagesLoading,
  } = usePhaseStageCodesForModality(modalityCode, activeForm?.form.code);

  const {
    data: interventionsForProblemAndModality,
    isLoading: isGetInterventionsLoading,
  } = useGetRefCarePlanInterventionsEnumQuery(
    { goalCodes: goalsForProblem?.map((goal) => goal.code), modalityCode },
    { skip: !goalsForProblem || !modalityCode },
  );

  const [
    createPathway,
    { isLoading: isCreatePathwayLoading, error: createPathwayError },
  ] = useCreateCarePlanMutation();
  const [
    createProblem,
    {
      isLoading: isCreateProblemLoading,
      isSuccess: wasCreateProblemSuccessful,
      error: createProblemError,
    },
  ] = useCreateProblemMutation();

  useEffect(() => {
    if (!selectedProblem) {
      setProblemDescription('');
      setSelectedManifestationsMap({});
    }
  }, [selectedProblem]);

  useEffect(() => {
    if (createProblemError) {
      createToast(CREATE_PROBLEM_TOAST_ARGS);
    }
  }, [createProblemError]);

  useEffect(() => {
    if (createPathwayError) {
      createToast(CREATE_PROBLEM_TOAST_ARGS);
    }
  }, [createPathwayError]);

  useEffect(() => {
    if (isGetManifestationsError) {
      createToast(CREATE_PROBLEM_TOAST_ARGS);
    }
  }, [isGetManifestationsError]);

  useEffect(() => {
    if (wasCreateProblemSuccessful) {
      closeModal();
    }
  }, [wasCreateProblemSuccessful]);

  const skipInterventions = isModalityOptional && !modalityCode;

  const handleCreateProblem = async () => {
    if (
      !selectedProblem ||
      !goalsForProblem ||
      (!skipInterventions &&
        (!interventionsForProblemAndModality || !phaseStagesForModality))
    ) {
      createToast(CREATE_PROBLEM_TOAST_ARGS);
      return;
    }
    const manifestations: CreateProblemRequest['problem']['manifestations'] =
      Object.entries(selectedManifestationsMap).map(
        ([pathwayManifestationCode, { description, frequencyCode }]) => ({
          pathwayManifestationCode,
          statusCode: RESOLVED_FREQUENCY_CODES.includes(frequencyCode)
            ? 'RESOLVED'
            : 'ACTIVE',
          frequencyCode,
          description,
        }),
      );

    const goals: CreateProblemRequest['problem']['goals'] = goalsForProblem.map(
      (goal) => ({
        goalCode: goal.code,
        description: null,
        statusCode: CarePlanGoalStatus.NOT_STARTED,
        interventions:
          skipInterventions || !interventionsForProblemAndModality
            ? []
            : interventionsForProblemAndModality.map((intervention) => ({
                interventionCode: intervention.code,
                statusCode: CarePlanInterventionStatus.PLANNED,
                phaseStageCode:
                  intervention.phaseStages.find((phaseStage) =>
                    phaseStagesForModality.some(
                      (psfm) => psfm.code === phaseStage.code,
                    ),
                  )?.code || null,
                providerTypes: intervention.providerTypes
                  .filter((pt) => pt.modalityCode === modalityCode)
                  .map((pt) => pt.providerType),
              })),
      }),
    );

    let newTreatmentPathwayId = '';
    if (!activeCarePlan) {
      try {
        const treatmentPathwayResponse = await createPathway({
          includeCurrent: false,
          statusCode: CarePlanStatus.ACTIVE,
          treatmentEpisodeId,
        }).unwrap();
        newTreatmentPathwayId = treatmentPathwayResponse.id;
      } catch (_) {
        return;
      }
    }

    createProblem({
      carePlanId: activeCarePlan?.id || newTreatmentPathwayId,
      treatmentEpisodeId,
      problem: {
        problemCode: selectedProblem.code,
        description: problemDescription,
        frequencyCode: problemFrequencyCode,
        manifestations,
        goals,
      },
    });
  };

  const shouldShowMissingAttributesError =
    !!modalityCode &&
    !isGetPhaseStagesLoading &&
    phaseStagesForModality.length === 0;

  const hasSelectedAtLeastOneManifestation =
    Object.keys(selectedManifestationsMap).length > 0;

  return (
    <ModalV2
      footerContent={
        selectedProblem !== null ? (
          <ChakraHStack
            justifyContent="space-between"
            paddingTop="14px"
            width="100%"
          >
            <FireflyButton
              id="add-problem-modal-back"
              leftIcon={FAIconName.chevronLeftRegular}
              onClick={() => {
                trackEvent(CarePlanEvents.clickOnBackButtonInAddProblemModal);
                setSelectedProblem(null);
              }}
              variant="secondary"
            >
              Back
            </FireflyButton>
            <FireflyButton
              id="add-problem-modal-add"
              isDisabled={
                manifestationsForED?.length > 0 &&
                !hasSelectedAtLeastOneManifestation
              }
              isLoading={
                isGetManifestationsLoading ||
                isFrequenciesLoading ||
                isGetGoalsLoading ||
                (!skipInterventions && isGetInterventionsLoading) ||
                (!skipInterventions && isGetPhaseStagesLoading) ||
                isCreatePathwayLoading ||
                isCreateProblemLoading
              }
              leftIcon={FAIconName.checkRegular}
              onClick={() => {
                handleCreateProblem();
                trackEvent(
                  CarePlanEvents.clickOnAddToCarePlanButtonInAddProblemModal,
                );
              }}
            >
              Add to care plan
            </FireflyButton>
          </ChakraHStack>
        ) : undefined
      }
      id={TOP_LEVEL_ID}
      isOpen
      onClose={() => {
        closeModal();
        trackEvent(CarePlanEvents.clickOnCloseIconInAddProblemModal);
      }}
      size="3xl"
      title="Add ED problem"
    >
      {shouldShowMissingAttributesError && (
        <ActionBar
          barIcon={FAIconName.exclamationTriangleRegular}
          id="add-problem-modal-no-ps"
          text="This patient may be missing a form or phase/stage attribute. Please add one before adding a problem."
          variant="critical"
        />
      )}
      {!shouldShowMissingAttributesError && (
        <>
          {selectedProblem === null && (
            <AddProblemModalSelectProblemStep
              patientId={patientId}
              problemTypeCode={problemTypeCode}
              setSelectedProblem={setSelectedProblem}
              treatmentEpisodeId={treatmentEpisodeId}
            />
          )}
          {selectedProblem !== null &&
            (isGetManifestationsLoading || isFrequenciesLoading ? (
              <Loading />
            ) : (
              <AddProblemModalManifestationsStep
                manifestationsForED={manifestationsForED ?? []}
                modalId={TOP_LEVEL_ID}
                problem={selectedProblem}
                problemDescription={problemDescription}
                problemFrequencyCode={problemFrequencyCode}
                selectedManifestationsMap={selectedManifestationsMap}
                setProblemDescription={setProblemDescription}
                setProblemFrequencyCode={setProblemFrequencyCode}
                setSelectedManifestationsMap={setSelectedManifestationsMap}
              />
            ))}
        </>
      )}
    </ModalV2>
  );
};
